import dayjs from "~/utils/dayjs";

/**
 * Client half of the server's admin-audit read contract (`GET /admin/audit`
 * and `GET /user/management/audit`): plain JSON, newest first, no
 * `ServerResponse` envelope, so responses are read straight off the wire.
 * The management read is place-scoped: it refuses a query carrying no
 * `location`, and reads `AllLocations` there as the whole org.
 */

export const AUDITED_FEATURE_IDS = [
  "administration",
  "management",
  "gmp-lamb-data-analysis",
  "gmp-lamb-bookings",
  "camera.aus-meat-omm",
  "camera.mb-gateway-movement",
  "dao.producers",
] as const;

export type AuditFeatureId = (typeof AUDITED_FEATURE_IDS)[number];

export type AuditOutcome = "applied" | "refused";

export type AuditSuperRung = "super-admin" | "super-user";

export type AuditCapability = "user" | "admin";

export interface AuditActorAssignment {
  org: string;
  location: string;
  capability: AuditCapability;
}

export interface AuditActor {
  userId: string | null;
  email: string | null;
  super: AuditSuperRung | null;
  assignment: AuditActorAssignment | null;
}

export interface AuditTarget {
  kind: string;
  id: string;
  label: string | null;
}

/** A side too large to keep is replaced whole, so the marker is never nested. */
export interface AuditTruncatedSide {
  truncated: true;
  bytes: number;
}

export type AuditSide = Record<string, unknown> | AuditTruncatedSide;

/**
 * The server writes an empty side as `{}`, but Mongoose minimises empty
 * objects away on save and the read is `.lean()`, so a create, delete or
 * refused action reaches the wire with that side missing altogether.
 */
export type AuditSideOnWire = AuditSide | undefined;

export interface AdminAuditEntry {
  _id: string;
  at: string;
  requestId: string;
  actor: AuditActor;
  feature: AuditFeatureId;
  action: string;
  target: AuditTarget;
  org: string;
  location: string | null;
  before: AuditSideOnWire;
  after: AuditSideOnWire;
  outcome: AuditOutcome;
}

export interface AdminAuditResponse {
  entries: AdminAuditEntry[];
  total: number;
}

export const isAuditSideTruncated = (
  side: AuditSideOnWire,
): side is AuditTruncatedSide =>
  side !== undefined && "truncated" in side && side.truncated === true;

export const auditTruncatedKilobytes = (bytes: number): number =>
  Math.round(bytes / 1024);

/**
 * Feature ids are dotted, and i18next reads a dot as a key separator, so the
 * lookup key folds the dots away rather than nesting the copy.
 */
export const auditFeatureLabelKey = (feature: string): string =>
  `features.${feature.replace(/\./g, "-")}`;

export interface AuditDiffRow {
  field: string;
  /** null where the field is absent on that side — a create or a delete. */
  before: string | null;
  after: string | null;
}

const formatAuditValue = (value: unknown): string => JSON.stringify(value);

const changedFieldsOf = (side: AuditSideOnWire): Record<string, unknown> =>
  side === undefined || isAuditSideTruncated(side) ? {} : side;

export const buildAuditDiffRows = (
  before: AuditSideOnWire,
  after: AuditSideOnWire,
): AuditDiffRow[] => {
  const beforeFields = changedFieldsOf(before);
  const afterFields = changedFieldsOf(after);
  const fields = [
    ...Object.keys(beforeFields),
    ...Object.keys(afterFields).filter((field) => !(field in beforeFields)),
  ];

  return fields.map((field) => ({
    field,
    before:
      field in beforeFields ? formatAuditValue(beforeFields[field]) : null,
    after: field in afterFields ? formatAuditValue(afterFields[field]) : null,
  }));
};

export interface AuditFilters {
  actor?: string | null;
  target?: string | null;
  feature?: string | null;
  from?: string | null;
  to?: string | null;
}

export interface AuditPaging {
  page: number;
  pageSize: number;
}

export type AuditQueryParams = Record<string, string | number>;

const trimmed = (value: string | null | undefined): string | undefined => {
  const next = value?.trim();
  return next ? next : undefined;
};

export const buildAuditQueryParams = (
  filters: AuditFilters,
  { page, pageSize }: AuditPaging,
): AuditQueryParams => {
  const actor = trimmed(filters.actor);
  const target = trimmed(filters.target);
  const feature = trimmed(filters.feature);
  const from = trimmed(filters.from);
  const to = trimmed(filters.to);

  return {
    ...(actor === undefined ? {} : { actor }),
    ...(target === undefined ? {} : { target }),
    ...(feature === undefined ? {} : { feature }),
    ...(from === undefined
      ? {}
      : { from: dayjs(from).startOf("day").toISOString() }),
    // The server compares `to` with `$lte`, so a bare calendar date would
    // drop everything recorded after midnight on the day the user picked.
    ...(to === undefined ? {} : { to: dayjs(to).endOf("day").toISOString() }),
    page,
    pageSize,
  };
};

/** `useOrgs().getOrgShortName()` stands this in when no org is current. */
const NO_ORG_SHORT_NAME = "NotValidOrg";

/**
 * Customer key the management audit endpoint filters on. The server folds
 * case itself; folding here as well keeps the react-query key stable when the
 * same org arrives spelled differently.
 */
export const toAuditOrg = (
  orgShortName: string | undefined,
): string | undefined =>
  !orgShortName || orgShortName === NO_ORG_SHORT_NAME
    ? undefined
    : orgShortName.toLowerCase();
